import { getVaultMetadataByKey } from '@/constants/nucleus-vaults';
import {
  MS_IN_SECOND,
  QueryKey,
  STALE_TIME_SHORT,
  wrappedPlumeDecimals,
  wrappedPlumeTokenAddress,
} from '@/constants/shared';
import { nucleusClient } from '@/network/clients/nucleus';
import { roycoClient } from '@/network/clients/royco';
import { TNucleusRoycoPosition } from '@/pages/OptimizerVault/types';
import { VaultKey } from '@molecularlabs/nucleus-frontend';
import { useQuery } from '@tanstack/react-query';
import { formatUnits } from 'viem';
import { useAccount, useChainId } from 'wagmi';

export const useNucleusRoycoPositionsData = () => {
  const { address } = useAccount();
  const { data, isLoading: isLoadingRoycoPositionsData } = useQuery({
    queryKey: [QueryKey.RoycoPositionsFromNucleus, address],
    queryFn: async () => {
      if (!address) return null;
      const roycoPositionsData = await nucleusClient.getRoycoPortfolio({ userAddress: address });
      const {
        totalPlumeEarned,
        totalBalanceInUsd,
        totalBalance,
        totalPlumeClaimable,
        totalPlumeEarnedRaw,
        totalPlumeClaimableRaw,
      } = (roycoPositionsData ?? []).reduce(
        (total, nextPosition) => {
          if (!countsTowardsBalance(nextPosition)) return total;
          const { unlockTimestamp } = nextPosition;
          const isUnlocked = Date.now() > Number(unlockTimestamp) * MS_IN_SECOND;

          const plumeEarnedAmount = nextPosition.receivedIncentives?.[wrappedPlumeTokenAddress] ?? 0;
          const plumeClaimableAmount = isUnlocked ? plumeEarnedAmount : 0;

          const newTotalPlumeEarnedRaw = total.totalPlumeEarnedRaw + plumeEarnedAmount;
          const newTotalPlumeClaimableRaw = total.totalPlumeClaimableRaw + plumeClaimableAmount;
          const newTotalBalanceInUsd = total.totalBalanceInUsd + nextPosition.balanceInUsd;
          const newTotalBalance = total.totalBalance + nextPosition.balanceScaledDown;

          return {
            totalPlumeEarned: Number(formatUnits(BigInt(newTotalPlumeEarnedRaw), wrappedPlumeDecimals)),
            totalPlumeClaimable: Number(formatUnits(BigInt(newTotalPlumeClaimableRaw), wrappedPlumeDecimals)),
            totalBalanceInUsd: newTotalBalanceInUsd,
            totalBalance: newTotalBalance,
            totalPlumeEarnedRaw: newTotalPlumeEarnedRaw,
            totalPlumeClaimableRaw: newTotalPlumeClaimableRaw,
          };
        },
        {
          totalPlumeEarned: 0,
          totalPlumeClaimable: 0,
          totalPlumeEarnedRaw: 0,
          totalPlumeClaimableRaw: 0,
          totalBalance: 0,
          totalBalanceInUsd: 0,
        },
      );
      return {
        roycoPositionsData,
        totalPlumeEarned,
        totalPlumeClaimable,
        totalBalanceInUsd,
        totalBalance,
        totalPlumeEarnedRaw,
        totalPlumeClaimableRaw,
      };
    },
    refetchInterval: STALE_TIME_SHORT,
    refetchOnMount: false,
    enabled: Boolean(address),
  });
  const {
    totalPlumeClaimable = 0,
    totalPlumeEarned = 0,
    totalBalance = 0,
    totalBalanceInUsd = 0,
    totalPlumeEarnedRaw = 0,
    totalPlumeClaimableRaw = 0,
    roycoPositionsData = [],
  } = data ?? {};

  return {
    totalPlumeEarned,
    totalPlumeClaimable,
    totalBalanceInUsd,
    totalBalance,
    totalPlumeEarnedRaw,
    totalPlumeClaimableRaw,
    roycoPositionsData,
    isLoadingRoycoPositionsData,
    countsTowardsBalance,
  };
};

const countsTowardsBalance = (position: TNucleusRoycoPosition) => {
  const isClaimed = position.isClaimed.some(Boolean);
  const { isForfeited, isWithdrawn } = position;
  return !isClaimed && !isForfeited && !isWithdrawn;
};

export const useRoycoPositionDataForVaultKey = ({ vaultKey }: { vaultKey: VaultKey }) => {
  const vaultMetadata = getVaultMetadataByKey(vaultKey);
  const { vaultAddress, marketId } = vaultMetadata;
  const chainId = useChainId();
  const { address } = useAccount();
  const { data, isPending: isPendingCurrentVaultRoycoPositionData } = useQuery({
    queryKey: [QueryKey.RoycoPositionsForVaultKey, vaultAddress, address],
    queryFn: async () => {
      if (!address || !vaultAddress || !marketId) return null;
      const position = await roycoClient.getActivePositionsForUserInMarket({
        address,
        marketId,
        chainId: String(chainId),
      });
      const { lockedInputToken, unclaimedIncentiveTokens, claimedIncentiveTokens, incentiveTokens } = position;

      const positions = lockedInputToken.map((_lockedInputToken) => {
        const relevantUnclaimedIncentives = unclaimedIncentiveTokens.filter(
          (unclaimedIncentiveToken) => unclaimedIncentiveToken.weirollWallet === _lockedInputToken.weirollWallet,
        );
        const relevantClaimedIncentives = claimedIncentiveTokens.filter(
          (claimedIncentiveToken) => claimedIncentiveToken.weirollWallet === _lockedInputToken.weirollWallet,
        );
        const relevantUnclaimedWPlumeIncentive = unclaimedIncentiveTokens.find(
          (unclaimedIncentiveToken) =>
            unclaimedIncentiveToken.weirollWallet === _lockedInputToken.weirollWallet &&
            unclaimedIncentiveToken.contractAddress === wrappedPlumeTokenAddress,
        );
        const relevantClaimedWPlumeIncentive = claimedIncentiveTokens.find(
          (claimedIncentiveToken) =>
            claimedIncentiveToken.weirollWallet === _lockedInputToken.weirollWallet &&
            claimedIncentiveToken.contractAddress === wrappedPlumeTokenAddress,
        );
        return {
          id: _lockedInputToken.weirollWallet,
          lockedInputToken: _lockedInputToken,
          unclaimedIncentiveTokens: relevantUnclaimedIncentives,
          claimedIncentiveTokens: relevantClaimedIncentives,
          unclaimedWPlumeIncentiveToken: relevantUnclaimedWPlumeIncentive,
          claimedWPlumeIncentiveToken: relevantClaimedWPlumeIncentive,
        };
      });
      return {
        currentVaultRoycoPositionData: positions.sort(
          (a, b) => Number(a.lockedInputToken.unlockTimestamp) - Number(b.lockedInputToken.unlockTimestamp),
        ),
        // "incentiveTokens" in specific position include all the unclaimed liquid incentives + claimed point incentives
        wPlumeIncentiveTotals: incentiveTokens.find(
          ({ contractAddress }) => contractAddress === wrappedPlumeTokenAddress,
        ),
        inputTokenTotals: position.inputToken,
      };
    },
  });
  const { currentVaultRoycoPositionData, wPlumeIncentiveTotals, inputTokenTotals } = data ?? {};
  return {
    currentVaultRoycoPositionData,
    isPendingCurrentVaultRoycoPositionData,
    wPlumeEarnings: wPlumeIncentiveTotals?.tokenAmount ?? 0,
    wPlumeEarningsUsd: wPlumeIncentiveTotals?.tokenAmountUsd ?? 0,
    wPlumeIncentiveTotals,
    inputTokenTotals,
  };
};

/**
 *
 * Why don't we use this hook to calculate royco totals everywhere?
 * !!! Because !!!
 * This hook gets royco data for the user across *all* royco markets, including
 * NON-OPTIMIZER RELATED ONES.
 *
 * This is why we do a bit of calculation and grab it from the other hooks sometimes.
 */
export const useRoycoTotals = () => {
  const { address } = useAccount();
  const { data: roycoGlobalData, isPending } = useQuery({
    queryKey: [QueryKey.RoycoPositions, address],
    queryFn: async () => {
      if (!address) return null;
      const roycoPositions = await roycoClient.getGlobals({ userAddress: address });
      return {
        ...roycoPositions,
        incentiveBalanceWPlume: roycoPositions.incentiveTokens.reduce((total, nextToken) => {
          if (nextToken.contractAddress !== wrappedPlumeTokenAddress) return total;
          return total + nextToken.tokenAmount;
        }, 0),
      };
    },
  });
  return { roycoGlobalData, isPendingRoycoGlobalData: isPending };
};
